import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import HighlightOffOutlinedIcon from "@mui/icons-material/HighlightOffOutlined";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Link from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import iconExternalLink from "../../../assets/icon-ExternalLink.svg";
import iconLinkSlant from "../../../assets/icon-link-slant.svg";
import { brandContainedButtonSx } from "../../../constants/brandContainedButtonStyles";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useModuleDisplayName } from "../../../hooks/useModuleDisplayName";
import {
  aiHubContainedButtonSx,
  aiHubOutlinedButtonSx,
  getAiHubStatusChipSx,
} from "../aiHubChipStyles";
import { AI_HUB_PATH } from "../constants";
import TriageResultDetailBreadcrumb from "./TriageResultDetailBreadcrumb";
import {
  formatTriageDetailFlowStatusLabel,
  formatTriageDetailSummaryLine,
  getTriageResultDetail,
  getTriageObjectTypeBreadcrumbLabel,
} from "./triageResultDetailData";

const REFERENCE_LINKS = [
  "Classification results",
  "Conditional Classification results",
  "Regulation details page",
] as const;

const fieldLabelSx = {
  fontSize: 12,
  color: "#6b7280",
  mb: 0.75,
} as const;

const fieldValueSx = {
  fontSize: 14,
  color: "#1f2937",
  fontWeight: 500,
} as const;

function ReferenceDocumentButton({ label }: { label: string }) {
  return (
    <Box
      component="button"
      type="button"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 1,
        padding: "6px 12px",
        bgcolor: "#EFF6FF",
        border: "none",
        borderRadius: "4px",
        cursor: "pointer",
        font: "inherit",
        fontSize: 12,
        color: "#295EF1",
      }}
    >
      <Box
        component="img"
        src={iconLinkSlant}
        alt=""
        sx={{ width: 12, height: 12, display: "block" }}
      />
      {label}
      <Box
        component="img"
        src={iconExternalLink}
        alt=""
        sx={{ width: 12, height: 12, display: "block" }}
      />
    </Box>
  );
}

export default function TriageResultDetailPage() {
  const navigate = useNavigate();
  const { resultId = "" } = useParams();
  const { showMessage } = useSnackbar();
  const moduleDisplayName = useModuleDisplayName();

  const detail = useMemo(() => getTriageResultDetail(resultId), [resultId]);

  const returnToList = () => {
    navigate(AI_HUB_PATH, { state: { tab: "triageResult" } });
  };

  if (!detail) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          bgcolor: "#f7f8fa",
          p: { xs: 2, md: 3 },
          boxSizing: "border-box",
        }}
      >
        <Typography sx={{ color: "#6b7280", fontSize: 14 }}>
          Triage result not found.
        </Typography>
        <Button sx={{ mt: 2 }} onClick={returnToList}>
          {`Back to ${moduleDisplayName}`}
        </Button>
      </Box>
    );
  }

  const flowStatusLabel = formatTriageDetailFlowStatusLabel(detail.flowStatus);

  return (
    <Box
      sx={{
        minHeight: "100vh",
        bgcolor: "#f7f8fa",
        p: { xs: 2, md: 3 },
        boxSizing: "border-box",
      }}
    >
      <TriageResultDetailBreadcrumb
        objectTypeLabel={getTriageObjectTypeBreadcrumbLabel(
          detail.objectTypeName,
        )}
        objectId={detail.objectId}
        resultId={detail.resultId}
        onBack={returnToList}
        onObjectTypeClick={returnToList}
      />

      <Box
        sx={{
          bgcolor: "#fff",
          borderRadius: "8px",
          border: "1px solid #eef0f3",
          p: { xs: 2, md: 3 },
        }}
      >
        <Typography
          component="h1"
          sx={{
            maxWidth: 900,
            fontSize: 20,
            lineHeight: 1.6,
            fontWeight: 600,
            color: "#1f2937",
          }}
        >
          {detail.title}
        </Typography>
        <Typography sx={{ mt: 1, fontSize: 13, color: "#6b7280" }}>
          {formatTriageDetailSummaryLine(detail)}
        </Typography>

        <Typography sx={{ mt: 2.5, mb: 0.75, fontSize: 12, color: "#6b7280" }}>
          Reference document
        </Typography>
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
          {REFERENCE_LINKS.map((label) => (
            <ReferenceDocumentButton key={label} label={label} />
          ))}
        </Box>

        <Box
          sx={{
            mt: 3,
            pt: 2.5,
            borderTop: "1px solid #e5e7eb",
            display: "flex",
            flexWrap: "wrap",
            gap: 3,
            alignItems: "flex-start",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              flexWrap: "wrap",
              gap: { xs: 2.5, md: 4 },
              flex: 1,
              minWidth: 280,
            }}
          >
            <Box>
              <Typography sx={fieldLabelSx}>Matched Flow</Typography>
              <Link
                component="button"
                type="button"
                underline="always"
                sx={{
                  ...fieldValueSx,
                  color: "#295EF1",
                  cursor: "pointer",
                  textAlign: "left",
                }}
                onClick={() =>
                  showMessage("Matched flow detail is coming soon.", "info")
                }
              >
                {detail.matchedFlow}
              </Link>
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Approve?</Typography>
              <Chip
                size="small"
                label={
                  detail.approvalStatus === "approved"
                    ? "Approved"
                    : "Unapproved"
                }
                sx={getAiHubStatusChipSx(
                  detail.approvalStatus === "approved" ? "green" : "yellow",
                )}
              />
            </Box>
            <Box>
              <Typography sx={fieldLabelSx}>Flow status</Typography>
              <Chip
                size="small"
                label={flowStatusLabel}
                sx={getAiHubStatusChipSx(
                  detail.flowStatus === "completed"
                    ? "green"
                    : detail.flowStatus === "inProgress"
                      ? "blue"
                      : detail.flowStatus === "terminate"
                        ? "red"
                        : "yellow",
                )}
              />
            </Box>
          </Box>

          <Box sx={{ display: "flex", gap: 1, flexShrink: 0 }}>
            <Button
              variant="outlined"
              sx={{
                ...aiHubOutlinedButtonSx,
                color: "#E30000",
                borderColor: "#E30000",
                "&:hover": {
                  borderColor: "#E30000",
                  bgcolor: "rgba(227, 0, 0, 0.04)",
                },
              }}
              startIcon={<HighlightOffOutlinedIcon />}
              onClick={() =>
                showMessage("Reject flow is coming soon.", "info")
              }
            >
              Reject
            </Button>
            <Button
              variant="contained"
              sx={{ ...brandContainedButtonSx, ...aiHubContainedButtonSx }}
              startIcon={<CheckCircleOutlineIcon />}
              onClick={() =>
                showMessage("Approve & Initiate is coming soon.", "info")
              }
            >
              Approve & Initiate
            </Button>
          </Box>
        </Box>

        <Box sx={{ mt: 3 }}>
          <Typography sx={fieldLabelSx}>Matched Rule ID</Typography>
          <Link
            component="button"
            type="button"
            underline="always"
            sx={{
              ...fieldValueSx,
              color: "#295EF1",
              cursor: "pointer",
              textAlign: "left",
            }}
            onClick={() =>
              showMessage("Triage rule detail is coming soon.", "info")
            }
          >
            {detail.matchedRuleId}
          </Link>
        </Box>

        <Box sx={{ mt: 2.5 }}>
          <Typography sx={fieldLabelSx}>Matched Conditions</Typography>
          <Typography sx={{ ...fieldValueSx, fontWeight: 400, lineHeight: 1.6 }}>
            {detail.matchedConditions}
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
